import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { fetchQuotaStats } from '../../api/dashboardApi';
import { DivideCircle } from 'lucide-react';

// Helper function to format currency values
const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'LKR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

const QuotaDashboard = ({startDate, setStartDate, endDate, setEndDate}) => {
  const [quotaData, setQuotaData] = useState([]);
  const [groupedData, setGroupedData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalRevenue, setTotalRevenue] = useState(0);
  
  // Fetch data and process it when component mounts or date range changes
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Call the API to get quota stats
        const data = await fetchQuotaStats(startDate, endDate);
        setQuotaData(data);
        
        // Group data by course title
        const grouped = data.reduce((acc, item) => {
          const { courseTitle } = item;
          if (!acc[courseTitle]) {
            acc[courseTitle] = [];
          }
          acc[courseTitle].push(item);
          return acc;
        }, {});
        
        setGroupedData(grouped);
        
        // Calculate total revenue across all quotas
        const total = data.reduce((sum, item) => {
          return sum + parseFloat(item.totalRevenue);
        }, 0);
        
        setTotalRevenue(total);
        setLoading(false);
      } catch (err) {
        setError('Failed to load quota data');
        setLoading(false);
        console.error(err);
      }
    };

    loadData();
  }, [startDate, endDate]);

  // Prepare data for chart - display top 5 courses by revenue
  const courseChartData = Object.entries(groupedData).map(([courseTitle, items]) => {
    const courseRevenue = items.reduce((sum, item) => sum + parseFloat(item.totalRevenue), 0);
    return {
      name: courseTitle.length > 20 ? courseTitle.substring(0, 20) + '...' : courseTitle,
      revenue: courseRevenue,
      fullName: courseTitle
    };
  }).sort((a, b) => b.revenue - a.revenue).slice(0, 5);

  if (loading) return <div className="flex justify-center p-8"><div className="text-lg">Loading data...</div></div>;
  if (error) return <div className="bg-red-100 p-4 rounded text-red-700">{error}</div>;

  return (
    <div className="bg-gray-50 pt-28">
      <h1 className="text-2xl font-bold mb-6">Course Revenue Dashboard</h1>
      
      {/* Date Range Selector */}
      <div className="hidden flex-wrap gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="border rounded p-2"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="border rounded p-2"
          />
        </div>
      </div>

      {/* Total Revenue Card */}
      <div className="bg-myBlue p-6 rounded-lg shadow-md mb-6">
        <h2 className="text-lg font-semibold text-white">Total Revenue</h2>
        <p className="text-2xl sm:text-3xl font-bold text-white mt-2">{formatCurrency(totalRevenue)}</p>
      </div>

      {/* Revenue Chart */}
      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <h2 className="text-lg font-semibold mb-4">Top 5 Courses by Revenue</h2>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={courseChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis tickFormatter={(value) => `LKR${value.toLocaleString()}`} />
              <Tooltip 
                formatter={(value) => formatCurrency(value)}
                labelFormatter={(label, payload) => payload[0]?.payload.fullName || label}
              />
              <Legend />
              <Bar dataKey="revenue" name="Revenue" fill="#0B8AD9" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Course Data Cards */}
      <div className="space-y-6">
        {Object.entries(groupedData).map(([courseTitle, items]) => {
          // Calculate total revenue for this course
          const courseTotalRevenue = items.reduce(
            (sum, item) => sum + parseFloat(item.totalRevenue), 
            0
          );
          
          return (
            <div key={courseTitle} className="bg-white rounded-lg shadow-md overflow-hidden">
              {/* Course header with title and total */}
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-6 border-b gap-2">
                <h2 className="text-lg font-semibold text-center">{courseTitle}</h2>
                <div className="font-bold text-green-600">{formatCurrency(courseTotalRevenue)}</div>
              </div>
              
              {/* Quota table for this course */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quota Title</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {items.map((item) => (
                      <tr key={item.quota_id}>
                        <td className="px-6 py-4 whitespace-nowrap">{item.quotaTitle}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          {formatCurrency(parseFloat(item.totalRevenue))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default QuotaDashboard;